/**
 * Scarico del documento di offerta.
 *
 * I contenuti (standard, attività, fasi, giornate) si compilano nel modulo
 * dell'offerta e sono già salvati: qui si sceglie solo il formato. Restano
 * modificabili le voci che possono cambiare proprio al momento dell'invio —
 * validità, data di avvio prevista e condizioni di pagamento.
 */
import { useState } from 'react';
import Modal from './Modal';
import { documentoApi } from '../api';
import { daTesto, TERMINI_PREDEFINITI } from './PannelloDocumento';

const label = 'block text-xs font-medium text-gray-500 mb-0.5';
const gg = (n) => String(Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(1)).replace('.', ',');

export default function GeneraDocumento({ offerta, onClose }) {
  const doc = offerta.documento || {};
  const [validita, setValidita] = useState(offerta.data_scadenza?.slice(0, 10) || '');
  const [avvio, setAvvio] = useState(doc.data_inizio_prevista || '');
  // Modalità e termini di pagamento: spesso si concordano proprio mentre si
  // manda l'offerta, quindi si ritoccano qui senza rientrare nella scheda.
  const [pagamento, setPagamento] = useState(doc.pagamento || 'SAL 100% a fine attività');
  const [termini, setTermini] = useState(doc.pagamento_termini || TERMINI_PREDEFINITI);
  const [lavorando, setLavorando] = useState(false);
  // La firma depositata di chi emette entra nel documento. Si può togliere:
  // una bozza da far rivedere non ha motivo di uscire già firmata.
  const [firma, setFirma] = useState(true);

  const compilato = !!(doc.attivitaTesto || (doc.fasi || []).length || doc.preventivo?.attivo);
  // Il Gantt esce se lo si è chiesto e ci sono fasi da disegnare
  const conGantt = doc.mostraGantt !== false && (doc.fasi || []).length > 0;
  const conDettaglio = !!(doc.preventivo?.attivo && doc.preventivo?.mostraDettaglio);

  const scarica = async (formato) => {
    setLavorando(true);
    try {
      await documentoApi.scarica(offerta.id, {
        // Il server parte dai dati salvati con l'offerta: qui si inviano solo
        // le trasformazioni che il formato JSON dell'offerta non ha già pronte.
        attivita: daTesto(doc.attivitaTesto),
        fasi: (doc.fasi || []).map(x => ({
          nome: x.nome, durata: x.durata,
          inizio: Number(x.inizio) || 1, settimane: Number(x.settimane) || 1,
          giornate: Number(x.giornate) || 0,
          attivita: daTesto(x.attivitaTesto),
        })),
        mostraGantt: conGantt,
        // Dettaglio dell'impegno (attività e ore) nelle offerte a consuntivo
        mostraImpegno: doc.mostraImpegno !== false,
        // Tempi e costi: due scelte separate, decise nel pannello documento
        mostraTempi: doc.mostraTempi !== false,
        mostraCosti: doc.mostraCosti !== false,
        conFirma: firma,
        data_inizio_prevista: avvio || undefined,
        validita: validita || undefined,
        pagamento: pagamento || undefined,
        pagamento_termini: termini || undefined,
      }, formato, `Offerta_${String(offerta.numero).replace(/[^\w-]+/g, '')}`);
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  return (
    <Modal title={`Documento offerta — ${offerta.numero}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        {!compilato ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-gray-700">
            I contenuti del documento non sono ancora stati compilati.
            Apri <strong>Modifica</strong> su questa offerta e riempi la sezione
            <em> Contenuti del documento</em>: standard, attività e fasi restano
            salvati e non dovrai più reinserirli.
          </div>
        ) : (
          <div className="bg-gray-50 border rounded-lg p-3 text-sm space-y-1">
            <div className="text-gray-600">Cliente: <strong className="text-gray-900">{offerta.cliente_nome || '—'}</strong></div>
            <div className="text-gray-600">Oggetto: <strong className="text-gray-900">{offerta.titolo}</strong></div>
            <div className="text-gray-600">
              Importo: <strong className="text-gray-900">{Number(offerta.importo || 0).toLocaleString('it-IT')} €</strong>
              {(doc.fasi || []).length > 0 && <> · Fasi: <strong className="text-gray-900">{doc.fasi.length}</strong></>}
              {Number(offerta.giornate_previste) > 0 && (
                <> · Giornate previste: <strong className="text-brand-700">{gg(offerta.giornate_previste)}</strong></>
              )}
            </div>
            {/* Cosa conterrà il documento: detto prima di scaricarlo */}
            <div className="text-xs text-gray-500 pt-1">
              Cronoprogramma: <strong>{conGantt ? 'incluso' : 'non incluso'}</strong>
              {' · '}Compenso: <strong>{conDettaglio ? 'con tabella di dettaglio' : 'solo importo a corpo'}</strong>
              {pagamento && <>{' · '}Pagamento: <strong>{pagamento}</strong></>}
            </div>
          </div>
        )}

        {/* La firma depositata: c'è solo se chi ha emesso l'offerta l'ha
            caricata in Utenti e Accessi. Qui si può togliere per una bozza. */}
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="w-4 h-4 mt-0.5" checked={firma}
                 onChange={e => setFirma(e.target.checked)} />
          <span>
            Documento già firmato
            <span className="block text-xs text-gray-500">
              {firma
                ? 'Se chi ha emesso l’offerta ha depositato la firma, compare al posto della riga da firmare a mano. Quella per l’accettazione del cliente resta.'
                : 'Esce con la riga da firmare a mano, come una bozza da far rivedere.'}
            </span>
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label}>Offerta valida fino al</label>
            <input className="input text-sm" type="date" value={validita}
                   onChange={e => setValidita(e.target.value)} />
          </div>
          {(doc.fasi || []).length > 0 && (
            <div>
              <label className={label}>Avvio previsto (per il cronoprogramma)</label>
              <input className="input text-sm" type="date" value={avvio}
                     onChange={e => setAvvio(e.target.value)} />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <button className="btn-secondary" onClick={onClose}>Chiudi</button>
          <button className="btn-secondary" disabled={lavorando} onClick={() => scarica('docx')}>
            {lavorando ? '…' : '⬇ Word (.docx)'}
          </button>
          <button className="btn-primary" disabled={lavorando} onClick={() => scarica('pdf')}>
            {lavorando ? '…' : '⬇ PDF'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
